import React, { useEffect, useState, useRef, createRef, useMemo } from 'react';
import { useParams, useNavigate, useOutletContext } from 'react-router-dom';
import '../styles/CompanyDetail.css';
import '../styles/ModernDashboard.css';
import RatioDetailModal from '../components/RatioDetailModal';
import ChartModal from '../components/ChartModal';
import ForensicTab from '../components/ForensicTab';
import { parseForensicPayload } from '../utils/forensicDisplay';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import DCFInputs from '../components/DCFInputs';
import BuffettDCFInputs from '../components/BuffettDCFInputs';
import headerLogo from '../assets/branding/Header_logo.png';
import HeaderBackButton from '../components/HeaderBackButton';
import Select from 'react-select';
import PeerChartModal from '../components/PeerChartModal';
import RatioTrends from './RatioTrends';
import KPISection from './KPISection';  // ✅ Add this import
import CCCWaterfallChart from './CCCWaterfallChart';
import CashBridgeWaterfall from '../components/workingCapital/CashBridgeWaterfall';
import ReasonBanner from './ReasonBanner';
import AttributionBar from './AttributionBar';
import EvidenceChips from './EvidenceChips';
import Sparkline from '../components/Sparkline';
import { useLoader } from '../context/LoaderContext';
import ValuationPanel from '../components/ValuationPanel';
import AiResearchTab from '../components/AiResearchTab';
import NewsTab from '../components/NewsTab';
import AboutTab from '../components/AboutTab';
import AdminOperations from '../components/AdminOperations';
import AskFinlyticsChat from '../components/AskFinlyticsChat';
import InvestmentIntelligenceTab from '../components/InvestmentIntelligenceTab';
import PriceChartTab from '../components/PriceChartTab';
import TechnicalTab from '../components/TechnicalTab';
import { buildDisplayHeadersFromSet, formatRatioYears, extractYearSet } from '../utils/yearFormat';
import MetricInfoModal from '../components/MetricInfoModal';
import ErrorBoundary from '../components/ErrorBoundary';
import { getFinancialComparisonOptions } from '../config/comparisonConfig';
import { overlayStatementDerivedRatioValues } from '../utils/financialMetricResolver';
import { STATUS_LABELS } from '../config/sectorTaxonomy';
import { authHeaders } from '../auth/apiClient';
import { getCashFlowRatiosPanelMapping } from '../config/cashFlowRatiosPanelBenchmarkMap';
import { getProfitLossRatiosPanelMapping } from '../config/profitLossRatiosPanelBenchmarkMap';
import { getBalanceSheetRatiosPanelMapping } from '../config/balanceSheetRatiosPanelBenchmarkMap';
import {
    DISTRESS_FLAG_CAPTION,
    evaluateCashFlowRatiosPanel,
    evaluateDistressFlags,
    hasDistressFlagRule,
} from '../utils/cashFlowBenchmarkResolver';
import {
    evaluatePerYearProfitLossStatus,
    evaluateProfitLossRatiosPanel,
    injectRoicWaccSpreadRow,
} from '../utils/profitLossBenchmarkResolver';
import {
    dedupeRatiosByName,
    evaluateBalanceSheetRatiosPanel,
    evaluatePerYearBalanceSheetStatus,
} from '../utils/balanceSheetBenchmarkResolver';
import CashFlowStatusBadge from '../components/CashFlowStatusBadge';
import CashFlowStatementTable from '../components/CashFlowStatementTable';
import ProfitLossStatementTable from '../components/ProfitLossStatementTable';
import BalanceSheetStatementTable from '../components/BalanceSheetStatementTable';
import { formatRatioNameWithUnit, formatMarketCapFromRupees, formatSharesOutstanding } from '../utils/financialUnit';
import {
    getFinlyticsSelectMenuPortalProps,
    getFinlyticsSelectStyles,
} from '../styles/reactSelectStyles';

const CASH_FLOW_COMPARISON_OPTIONS = getFinancialComparisonOptions(['cashFlow']);
const PROFIT_LOSS_COMPARISON_OPTIONS = getFinancialComparisonOptions(['profitAndLoss', 'crossStatement']);
const BALANCE_SHEET_COMPARISON_OPTIONS = getFinancialComparisonOptions(['balanceSheet', 'crossStatement']);
const FINANCIAL_COMPARISON_SELECT_STYLES = getFinlyticsSelectStyles();
const FINANCIAL_COMPARISON_SELECT_PORTAL_PROPS = getFinlyticsSelectMenuPortalProps();

function FinancialComparisonsBlock({ intro, placeholder, options, onSelect }) {
    return (
        <div className="ratios-section" style={{ marginBottom: '12px' }}>
            <h3>Financial Comparisons</h3>
            <p style={{ color: 'var(--text-2)', fontSize: '13px', margin: '0 0 8px 0' }}>
                {intro}
            </p>
            <Select
                className="basic-single"
                classNamePrefix="select"
                styles={FINANCIAL_COMPARISON_SELECT_STYLES}
                options={options}
                placeholder={placeholder}
                value={null}
                onChange={(option) => onSelect(option?.value)}
                {...FINANCIAL_COMPARISON_SELECT_PORTAL_PROPS}
            />
        </div>
    );
}

const ABS_DISPLAY_CASHFLOW_ROWS = new Set(['dividendsToCfo', 'debtRepaymentToCfo']);

function benchmarkYearCell(rowStatus, yearStatus) {
    const scored = rowStatus === 'strong' || rowStatus === 'watch' || rowStatus === 'weak';
    if (!scored || !yearStatus || yearStatus === rowStatus) {
        return { className: undefined, title: undefined };
    }
    const yearLabel = STATUS_LABELS[yearStatus] || yearStatus;
    const rowLabel = STATUS_LABELS[rowStatus] || rowStatus;
    return {
        className: `fin-year-cell fin-year-cell--${yearStatus} fin-year-cell--outlier`,
        title: `This year: ${yearLabel} (row: ${rowLabel})`,
    };
}



const TABS = ['Price', 'Technical', 'Profit and Loss', 'Balance Sheet', 'Cash Flow', 'Forensic', 'DCF', 'Valuation', 'Peer Compare', 'Working Capital', 'Investment Intelligence', 'AI Research', 'Ask AI', 'News', 'About', 'Admin Operations'];

// Helper function to normalize KPIs data structure (UPDATED)
const normalizeKPIs = (kpisData) => {
    if (!kpisData) return [];

    // If already an array, return as-is
    if (Array.isArray(kpisData)) return kpisData;

    // Define all metrics configuration with comprehensive info
    const KEY_METRICS_CONFIG = [
        // Core cycle metrics
        {
            key: "dso",
            label: "DSO",
            unit: "d",
            better: "down",
            info: {
                purpose: "Measures how quickly a company collects cash from credit sales. Lower DSO means faster cash collection.",
                formula: "DSO = (Average Receivables ÷ Revenue) × 365",
                thresholds: {
                    excellent: "< 30 days - Very efficient collection",
                    good: "30-45 days - Healthy collection period",
                    moderate: "45-60 days - Industry average",
                    concern: "> 60 days - Slow collections, potential cash flow issues"
                }
            }
        },
        {
            key: "dio",
            label: "DIO",
            unit: "d",
            better: "down",
            info: {
                purpose: "Measures how long inventory sits before being sold. Lower DIO indicates efficient inventory management.",
                formula: "DIO = (Average Inventory ÷ COGS) × 365",
                thresholds: {
                    excellent: "< 30 days - Lean inventory, fast turnover",
                    good: "30-60 days - Efficient inventory management",
                    moderate: "60-90 days - Industry average",
                    concern: "> 90 days - Excess inventory, potential obsolescence risk"
                }
            }
        },
        {
            key: "dpo",
            label: "DPO",
            unit: "d",
            better: "up",
            info: {
                purpose: "Measures how long a company takes to pay suppliers. Higher DPO means retaining cash longer (good for liquidity).",
                formula: "DPO = (Average Payables ÷ COGS) × 365",
                thresholds: {
                    excellent: "> 60 days - Strong negotiating power with suppliers",
                    good: "45-60 days - Balanced payment terms",
                    moderate: "30-45 days - Standard payment period",
                    concern: "< 30 days - Weak bargaining position or liquidity pressure"
                }
            }
        },
        {
            key: "ccc",
            label: "CCC",
            unit: "d",
            better: "down",
            info: {
                purpose: "Net time between paying suppliers and collecting from customers. Lower is better for cash flow efficiency.",
                formula: "CCC = DSO + DIO - DPO",
                thresholds: {
                    excellent: "< 30 days - Exceptional working capital efficiency",
                    good: "30-60 days - Strong cash conversion",
                    moderate: "60-90 days - Average efficiency",
                    concern: "> 90 days - Cash tied up too long in operations"
                }
            }
        },
        // Efficiency / intensity metrics
        {
            key: "tradeNWC",
            label: "Trade NWC",
            unit: "Cr",
            better: "down",
            info: {
                purpose: "Total working capital invested in core operations. Lower relative to sales indicates efficiency.",
                formula: "Trade NWC = Receivables + Inventory - Payables",
                thresholds: {
                    note: "Compare with sales growth. If NWC grows slower than sales, efficiency is improving. Monitor the NWC % of Sales metric for better context."
                }
            }
        },
        {
            key: "nwcPctSales",
            label: "NWC % of Sales",
            unit: "%",
            better: "down",
            info: {
                purpose: "Working capital intensity - shows how much working capital is needed per rupee of sales.",
                formula: "NWC % = (Trade NWC ÷ Revenue) × 100",
                thresholds: {
                    excellent: "< 10% - Very capital efficient operations",
                    good: "10-20% - Efficient working capital management",
                    moderate: "20-30% - Industry average",
                    concern: "> 30% - High working capital intensity, potential inefficiency"
                }
            }
        },
        {
            key: "workingCapitalChange",
            label: "ΔWC Cash Impact",
            unit: "Cr",
            better: "up",
            info: {
                purpose: "Cash released or consumed by working capital changes. Positive = cash released (good), Negative = cash consumed (bad).",
                formula: "ΔWC = -(Current Trade NWC - Previous Trade NWC)",
                thresholds: {
                    excellent: "> 0 - Cash released from improved efficiency",
                    concern: "< 0 - Cash consumed (but acceptable if driven by growth)"
                }
            }
        },
        // Cash conversion quality
        {
            key: "cashConversionSales",
            label: "CFO / Sales",
            unit: "%",
            better: "up",
            info: {
                purpose: "Operating cash flow margin - measures how much cash is generated per rupee of sales.",
                formula: "CFO/Sales = (Operating Cash Flow ÷ Revenue) × 100",
                thresholds: {
                    excellent: "> 15% - Strong cash generation",
                    good: "10-15% - Healthy cash conversion",
                    moderate: "5-10% - Acceptable cash flow",
                    concern: "< 5% - Weak cash generation, quality of earnings issue"
                }
            }
        },
        {
            key: "cfoToEbitda",
            label: "CFO / EBITDA",
            unit: "x",
            better: "up",
            info: {
                purpose: "Measures how much of EBITDA converts to actual cash. Higher indicates better earnings quality.",
                formula: "CFO/EBITDA = Operating Cash Flow ÷ EBITDA",
                thresholds: {
                    excellent: "> 0.8x - Strong cash conversion",
                    good: "0.6-0.8x - Good earnings quality",
                    moderate: "0.4-0.6x - Acceptable but monitor",
                    concern: "< 0.4x - Poor cash conversion, aggressive accounting possible"
                }
            }
        },
        {
            key: "cfoToPat",
            label: "CFO / PAT",
            unit: "x",
            better: "up",
            info: {
                purpose: "Shows if reported profits translate to cash. >1.0 means company generates more cash than profit (excellent).",
                formula: "CFO/PAT = Operating Cash Flow ÷ Net Profit",
                thresholds: {
                    excellent: "> 1.2x - Cash generation exceeds reported profit",
                    good: "1.0-1.2x - Profits convert well to cash",
                    moderate: "0.7-1.0x - Acceptable conversion",
                    concern: "< 0.7x - Earnings quality issue, potential accounting red flag"
                }
            }
        },
        // Component ratios (optional - uncomment if needed)
        // {
        //     key: "receivablesToSales",
        //     label: "AR / Sales",
        //     unit: "x",
        //     better: "down",
        //     tooltip: "Receivables as multiple of sales"
        // },
        // {
        //     key: "inventoryToCogs",
        //     label: "Inventory / COGS",
        //     unit: "x",
        //     better: "down",
        //     tooltip: "Inventory as multiple of cost of goods sold"
        // },
        // {
        //     key: "payablesToCogs",
        //     label: "AP / COGS",
        //     unit: "x",
        //     better: "up",
        //     tooltip: "Payables as multiple of cost of goods sold"
        // },
    ];

    // Convert object to array using config
    return KEY_METRICS_CONFIG.map(config => {
        const value = kpisData[config.key];
        const delta = kpisData[`${config.key}Delta`];

        return {
            key: config.key,
            label: config.label,
            value: value ?? null,
            unit: config.unit,
            delta: delta ?? null,
            better: config.better,
            info: config.info
        };
    }).filter(metric => metric.value !== null); // Only include metrics with values
};

const CompanyDetail = () => {
    const { id } = useParams();
    const navigate = useNavigate();

    // Get shared state from layout context
    const context = useOutletContext();
    const getUserInitials = context?.getUserInitials ?? (() => 'U');
    const getDisplayName = context?.getDisplayName ?? (() => 'User');
    const getProfilePhotoUrl = context?.getProfilePhotoUrl ?? (() => '');
    const getProfilePhotoLoading = context?.getProfilePhotoLoading ?? (() => false);
    const profilePhotoUrl = getProfilePhotoUrl();
    const profilePhotoLoading = getProfilePhotoLoading();
    const handleMenuOpen = context?.handleMenuOpen ?? (() => { });
    const contextIsDarkMode = context?.isDarkMode;
    const contextToggleTheme = context?.toggleTheme;

    const [basis, setBasis] = useState(() => localStorage.getItem('basis') || 'consolidated');
    const [financials, setFinancials] = useState(null);
    const [companyName, setCompanyName] = useState('');
    const [cashflowRatios, setCashflowRatios] = useState([]);
    const [profitlossRatios, setProfitlossRatios] = useState([]);
    const [balancesheetRatios, setBalancesheetRatios] = useState([]);
    const [forensicRatios, setForensicRatios] = useState([]);
    const [forensicReport, setForensicReport] = useState(null);
    const [activeTab, setActiveTab] = useState(TABS[0]);
    const [selectedRatio] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [chartRatio, setChartRatio] = useState(null);
    const [chartComparisonId, setChartComparisonId] = useState(null);
    const [showChartModal, setShowChartModal] = useState(false);
    const [metricInfoKey, setMetricInfoKey] = useState(null);
    const [showMetricInfo, setShowMetricInfo] = useState(false);
    const [isDarkMode, setIsDarkMode] = useState(() => contextIsDarkMode ?? localStorage.getItem('theme') === 'dark');
    const [indicatorStyle, setIndicatorStyle] = useState({});
    const [wcExplain, setWcExplain] = useState(null);

    // Handler for tab changes with logging
    const handleTabChange = (tab) => {
        console.log(`[CompanyDetail] Changing tab from "${activeTab}" to "${tab}"`);
        setActiveTab(tab);
    };

    const openChart = (nextRatio) => {
        setChartComparisonId(null);
        setChartRatio(nextRatio);
        setShowChartModal(true);
    };

    const openFinancialComparison = (nextComparisonId) => {
        if (!nextComparisonId) return;
        setChartRatio(null);
        setChartComparisonId(nextComparisonId);
        setShowChartModal(true);
    };

    const closeChartModal = () => {
        setShowChartModal(false);
        setChartRatio(null);
        setChartComparisonId(null);
    };

    // Sync dark mode with context
    const toggleTheme = contextToggleTheme ?? (() => setIsDarkMode(prev => !prev));

    const tabRefs = useRef(TABS.map(() => createRef()));

    // DCF-specific state
    const [fcfHistory, setFcfHistory] = useState([]);
    const [standardDcfInputs, setStandardDcfInputs] = useState(null); // <-- for standard DCF
    const [buffettDcfInputs, setBuffettDcfInputs] = useState(null);   // <-- for buffett DCF
    const [dcfPayload, setDcfPayload] = useState(null);
    const [dcfResult, setDcfResult] = useState(null);
    const [marketCap, setMarketCap] = useState(null);
    const [currentPrice, setCurrentPrice] = useState(null);
    const [, setMcHistory] = useState([]);
    const [histTermMultiple, setHistTermMultiple] = useState(0);
    const [dcfMode, setDcfMode] = useState('standard');
    const [advDcfInputs, setAdvDcfInputs] = useState({
        period: 10,
        years: 1,
        interval: 'annual',
        discount_rate: 0.11,
        earnings_growth_rate: 0.08,
        cap_ex_growth_rate: 0.03,
        perpetual_growth_rate: 0.025,
        step_increase: 0.0,
        steps: 0,
        variable: 'discount_rate'
    });
    const [advHistory, setAdvHistory] = useState([]);
    const [advSensitivity, setAdvSensitivity] = useState([]);
    const [proDcfDefaults, setProDcfDefaults] = useState({
        wacc: 0.12,
        projectionYears: 10,
        ltg: 0.025,
        exitMultiple: 12.0,
        exitMetric: 'FCF',
        baseFcfMethod: 'avg5',
        growthChoice: 'cagr5'
    });
    // Pro sensitivity state
    const [proSensMode, setProSensMode] = useState('ltg'); // 'ltg' | 'exitMultiple'
    const [proSensOutput, setProSensOutput] = useState('equityValuePerShare');
    const [proSensWaccStart, setProSensWaccStart] = useState(0.10);
    const [proSensWaccStop, setProSensWaccStop] = useState(0.14);
    const [proSensWaccStep, setProSensWaccStep] = useState(0.005);
    const [proSensYStart, setProSensYStart] = useState(0.01);
    const [proSensYStop, setProSensYStop] = useState(0.04);
    const [proSensYStep, setProSensYStep] = useState(0.005);
    const [proSensResult, setProSensResult] = useState(null);
    const [proMoS, setProMoS] = useState(0.0); // margin of safety decimal
    const [sharesOutstanding, setSharesOutstanding] = useState(null);
    const [wcData, setWcData] = useState(null);
    const [selectedCashBridgeYear, setSelectedCashBridgeYear] = useState(null);
    const [showSensitivity, setShowSensitivity] = useState(false); // controls advanced sensitivity panel visibility
    // Peer Compare state
    const [peersList, setPeersList] = useState([]);
    const [selectedPeerRatio, setSelectedPeerRatio] = useState('');
    const [selectedPeers, setSelectedPeers] = useState([]);
    const [showPeerChartModal, setShowPeerChartModal] = useState(false);
    const [sector, setSector] = useState('');
    const { show, hide } = useLoader();

    const API_URL = process.env.REACT_APP_API_BASE_URL || '/api';
    const unwrap = (payload) => (payload && typeof payload === 'object' && 'data' in payload ? payload.data : payload);


    useEffect(() => {
        document.body.setAttribute('data-theme', localStorage.getItem('theme'));

        show();
        fetch(`${API_URL}/finlytics/full-financials?company=${id}&basis=${basis}`)
            .then(res => res.json())
            .then(data => {
                const payload = unwrap(data);
                const fin = payload?.financials;
                const selected = fin?.[basis] || fin;
                setFinancials(selected);
                setCompanyName(payload?.companyName || id);
                setMarketCap(payload?.marketCap);
                setCurrentPrice(payload?.currentPrice);
                setSector(payload?.sector || payload?.industry || '');
            })
            .catch(console.error)
            .finally(() => hide());

        show();
        fetch(`${API_URL}/finlytics/ratios/${id}?basis=${basis}`)
            .then(res => res.json())
            .then(data => {
                const payload = unwrap(data);
                const ratiosForCashFlowTab = payload?.financialRatios?.find(r => r.tab === 'cashFlow');
                setCashflowRatios(ratiosForCashFlowTab?.ratios || []);
                const ratiosForProfitandLossTab = payload?.financialRatios?.find(r => r.tab === 'profitAndLoss');
                setProfitlossRatios(ratiosForProfitandLossTab?.ratios || []);
                const ratiosForBalancesheetTab = payload?.financialRatios?.find(r => r.tab === 'balanceSheet');
                setBalancesheetRatios(ratiosForBalancesheetTab?.ratios || []);
            })
            .catch(console.error)
            .finally(() => hide());

        show();
        fetch(`${API_URL}/finlytics/ratios/${id}/forensic?basis=${basis}`)
            .then(res => res.json())
            .then(data => {
                const parsed = parseForensicPayload(unwrap(data));
                setForensicReport(parsed.report);
                setForensicRatios(parsed.ratios || []);
            })
            .catch(console.error)
            .finally(() => hide());

        show();
        fetch(`${API_URL}/finlytics/shares-outstanding/${id}`)
            .then(res => res.json())
            .then(data => {
                const payload = unwrap(data);
                setSharesOutstanding(Number(payload?.sharesOutstanding));
            })
            .catch(err => console.error('Error fetching shares outstanding:', err))
            .finally(() => hide());

        show();
        fetch(`${API_URL}/finlytics/peers/${id}`)
            .then(res => res.json())
            .then(data => {
                const payload = unwrap(data);
                setPeersList(payload?.peers || []);
            })
            .catch(console.error)
            .finally(() => hide());

        show();
        setWcData(null);
        setSelectedCashBridgeYear(null);
        fetch(`${API_URL}/dashboard/working-capital-dashboard/${id}?basis=${basis}`)
            .then(res => res.json())
            .then(data => {
                const wcResult = unwrap(data);
                setWcData(wcResult);
                // Set default year to latest available
                if (wcResult?.years && wcResult.years.length > 0) {
                    const latestYear = wcResult.years[wcResult.years.length - 1];
                    setSelectedCashBridgeYear(latestYear);
                }
            })
            .catch(err => {
                setWcData({ error: true });
                console.error("Error fetching working capital dashboard:", err);
            })
            .finally(() => hide());

        show();
        setWcExplain(null);
        authHeaders()
            .then((headers) => fetch(`${API_URL}/finlytics/explain-for-company/${id}?basis=${basis}`, { headers }))
            .then(res => res.json())
            .then(data => setWcExplain(unwrap(data)))
            .catch(err => {
                setWcExplain({ error: true });
                console.error("Error fetching WC explain:", err);
            })
            .finally(() => hide());

    }, [id, basis, show, hide, API_URL]);

    useEffect(() => {
        // Fetch FCF and Market Cap history together
        Promise.all([
            fetch(`${API_URL}/finlytics/metrics/${id}/fcfAbsolute?basis=${basis}`).then(res => res.json()),
            fetch(`${API_URL}/finlytics/metrics/${id}/marketCapHistory`).then(res => res.json())
        ])
            .then(([fcfData, mcData]) => {
                const fcfPayload = unwrap(fcfData) || {};
                const mcPayload = unwrap(mcData) || {};
                // Sort ascending by year
                const fcfVals = (fcfPayload.values || []).slice().sort((a, b) => a.year - b.year);
                const mcVals = (mcPayload.values || []).slice().sort((a, b) => a.year - b.year);

                const fcfNums = fcfVals.map(o => Number(o.value) || 0);
                const mcNums = mcVals.map(o => Number(o.value) || 0);
                setFcfHistory(fcfNums);
                setMcHistory(mcNums);

                // Compute 5-year avg FCF multiple
                // (convert marketCap from ₹ → Crores so both series use the same unit)
                if (fcfNums.length >= 5 && mcNums.length >= 5) {
                    const last5Fcfs = fcfNums.slice(-5);
                    // marketCaps are in ₹ – divide by 1e7 to get Crores
                    const last5McsCr = mcNums.slice(-5).map(mc => mc / 1e7);

                    const multiples = last5McsCr.map((mcCr, i) =>
                        mcCr / last5Fcfs[i]
                    );
                    const avgMult = multiples.reduce((sum, m) => sum + m, 0) / multiples.length;
                    setHistTermMultiple(avgMult);
                }
            })
            .catch(err => console.error(err));
    }, [id, basis, API_URL]);

    // Submit DCF request (standard, buffett, pro, or advanced pro)
    useEffect(() => {
        if (!dcfPayload) return;
        let url = `${API_URL}/valuation/dcf`;
        let body = { symbol: id, marketCap, currentPrice, ...dcfPayload };
        if (dcfMode === 'buffett') {
            url = `${API_URL}/valuation/dcf/buffett`;
            body = { symbol: id, ...dcfPayload };
        } else if (dcfMode === 'pro') {
            url = `${API_URL}/valuation/dcf/pro/auto`;
            body = dcfPayload; // already a ProDCFRequest
        } else if (dcfMode === 'advPro') {
            url = `${API_URL}/valuation/dcf/advanced`;
            body = {
                ticker: id,
                period: dcfPayload.period,
                years: dcfPayload.years,
                interval: dcfPayload.interval,
                discount_rate: dcfPayload.discount_rate,
                earnings_growth_rate: dcfPayload.earnings_growth_rate,
                cap_ex_growth_rate: dcfPayload.cap_ex_growth_rate,
                perpetual_growth_rate: dcfPayload.perpetual_growth_rate,
                step_increase: dcfPayload.step_increase,
                steps: dcfPayload.steps,
                variable: dcfPayload.variable
            };
        }
        authHeaders({ 'Content-Type': 'application/json' })
            .then((headers) => fetch(url, {
                method: 'POST',
                headers,
                body: JSON.stringify(body)
            }))
            .then(res => res.json())
            .then(data => {
                const payload = unwrap(data);
                if (dcfMode === 'advPro') {
                    setAdvHistory(payload?.historical || []);
                    setAdvSensitivity(payload?.sensitivity || []);
                    const base = payload?.base;
                    if (base) {
                        setDcfResult({
                            intrinsicSharePrice: base.share_price,
                            equityValue: base.equity_value,
                            equityValuePerShare: base.share_price,
                            buyZoneSharePrice: null
                        });
                    } else {
                        setDcfResult(null);
                    }
                } else {
                    setDcfResult(payload);
                }
            })
            .catch(console.error);
    }, [dcfPayload, dcfMode, id, marketCap, currentPrice, API_URL]);

    useEffect(() => {
        const updateIndicator = () => {
            const activeTabIndex = TABS.indexOf(activeTab);
            const activeTabRef = tabRefs.current[activeTabIndex]?.current;
            if (!activeTabRef) return;
            setIndicatorStyle({
                left: activeTabRef.offsetLeft,
                width: activeTabRef.offsetWidth,
                top: activeTabRef.offsetTop + activeTabRef.offsetHeight - 2,
            });
        };
        updateIndicator();
        window.addEventListener('resize', updateIndicator);
        return () => window.removeEventListener('resize', updateIndicator);
    }, [activeTab, financials]);

    // Debug: Track activeTab changes
    useEffect(() => {
        console.log(`[CompanyDetail] activeTab changed to: "${activeTab}"`);
    }, [activeTab]);

    // Helpers to extract numeric series from financials
    const extractSeries = (section, label) => {
        const sec = financials?.[section];
        if (!sec || !sec[label]) return [];
        const obj = sec[label];
        const years = Object.keys(obj).filter(k => /^(19|20)\d{2}$/.test(k)).map(y => Number(y)).sort((a, b) => a - b);
        return years.map(y => parseNum(obj[String(y)]));
    };

    const buildCapexSeries = () => {
        const purchased = financials?.cashFlow?.['Fixed assets purchased'] || {};
        const sold = financials?.cashFlow?.['Fixed assets sold'] || {};
        const years = Array.from(new Set([
            ...Object.keys(purchased || {}),
            ...Object.keys(sold || {})
        ].filter(k => /^(19|20)\d{2}$/.test(k)).map(y => Number(y)))).sort((a, b) => a - b);
        return years.map(y => {
            const p = parseNum(purchased[String(y)] || 0); // typically negative
            const s = parseNum(sold[String(y)] || 0);       // typically positive
            return -(p + s); // convert to positive CapEx spend
        });
    };

    const latestFrom = (section, label) => {
        const arr = extractSeries(section, label);
        return arr.length ? arr[arr.length - 1] : null;
    };

    const buildProDcfRequest = () => {
        if (!financials) return null;

        // Historical series (Crores, oldest->newest)
        const ebit = extractSeries('profitAndLoss', 'Operating Profit');
        const da = extractSeries('profitAndLoss', 'Depreciation');
        const cfo = extractSeries('cashFlow', 'Cash from Operating Activity');
        const wc = extractSeries('cashFlow', 'Working capital changes');
        const capex = buildCapexSeries();

        // Tax % to decimals if available
        const taxPctSeries = (() => {
            const sec = financials?.profitAndLoss?.['Tax %'];
            if (!sec) return [];
            const years = Object.keys(sec).filter(k => /^(19|20)\d{2}$/.test(k)).map(y => Number(y)).sort((a, b) => a - b);
            return years.map(y => {
                const raw = String(sec[String(y)] || '').replace(/%/g, '').trim();
                const n = Number(raw);
                return isNaN(n) ? null : n / 100;
            });
        })();

        // Equity adjustments from balance sheet (latest year)
        const grossDebt = latestFrom('balanceSheet', 'Borrowings') || 0;
        const cashEq = latestFrom('balanceSheet', 'Cash Equivalents') || 0;
        const nci = latestFrom('balanceSheet', 'Non controlling int') || 0;

        // Choose FCFF method based on data availability
        const hasCfoCapex = cfo.length && capex.length;

        return {
            historical: {
                ebit: ebit.length ? ebit : null,
                depreciationAmortization: da.length ? da : null,
                capex: capex.length ? capex : null,
                changeInWorkingCapital: wc.length ? wc : null,
                cfo: cfo.length ? cfo : null,
                taxRateSeries: taxPctSeries.length ? taxPctSeries : null
            },
            fcffMethod: {
                method: hasCfoCapex ? 'cfoMinusCapex' : 'ebitBased',
                operatingTaxRate: taxPctSeries.filter(v => v != null).slice(-1)[0] ?? 0.25
            },
            projection: {
                baseFcfMethod: proDcfDefaults.baseFcfMethod,
                manualBaseFcf: null,
                growthChoice: proDcfDefaults.growthChoice,
                manualGrowthRate: null,
                clampMinGrowth: null,
                clampMaxGrowth: null,
                projectionYears: proDcfDefaults.projectionYears,
                useMidYearConvention: true
            },
            terminal: {
                longTermGrowth: proDcfDefaults.ltg,
                exitMultiple: proDcfDefaults.exitMultiple,
                exitMetric: proDcfDefaults.exitMetric,
                base: 'next'
            },
            waccInputs: {
                wacc: proDcfDefaults.wacc,
                parts: null,
                marketValueDebt: null,
                marketValueEquity: null
            },
            adjustments: {
                grossDebtAndEquivalents: grossDebt,
                cashAndEquivalents: cashEq,
                nonOperatingAssets: 0.0,
                nonControllingInterest: nci
            },
            sharesOutstandingDiluted: sharesOutstanding || null,
            marginOfSafety: proMoS,
            currency: 'INR',
            unitsAreCrores: true,
            includeAudit: true
        };
    };

    const runProSensitivity = async () => {
        const base = buildProDcfRequest();
        if (!base) return;
        try {
            const res = await fetch(`${API_URL}/valuation/dcf/pro/auto/sensitivity`, {
                method: 'POST',
                headers: await authHeaders({ 'Content-Type': 'application/json' }),
                body: JSON.stringify({
                    base,
                    params: {
                        mode: proSensMode,
                        waccRange: { start: proSensWaccStart, stop: proSensWaccStop, step: proSensWaccStep },
                        yRange: { start: proSensYStart, stop: proSensYStop, step: proSensYStep },
                        outputField: proSensOutput,
                        decimals: 2
                    }
                })
            });
            const data = await res.json();
            setProSensResult(unwrap(data));
        } catch (e) {
            console.error('Sensitivity error', e);
        }
    };

    // Call DCF API on calculate click
    const handleCalculate = () => {
        if (dcfMode === 'standard' && standardDcfInputs) {
            setDcfPayload(standardDcfInputs);
        } else if (dcfMode === 'buffett' && buffettDcfInputs) {
            setDcfPayload(buffettDcfInputs);
        } else if (dcfMode === 'pro') {
            const req = buildProDcfRequest();
            if (req) setDcfPayload(req);
        } else if (dcfMode === 'advPro') {
            setDcfPayload({ ...advDcfInputs });
        }
    };

    // helper: normalize various value formats to number (used by sparklines)
    const parseNum = (v) => {
        if (v == null) return 0;
        if (typeof v === 'number') return v;
        const raw = (typeof v === 'object' && v !== null) ? (v.value ?? '') : String(v);
        const cleaned = String(raw).replace(/[,\s₹]/g, '').replace(/%/g, '').replace(/[^\d.-]/g, '');
        return Number(cleaned) || 0;
    };

    // Distress flags must not treat missing cells as 0 (parseNum does).
    const parseFiniteOrNull = (v) => {
        if (v == null || v === '') return null;
        if (typeof v === 'number') return Number.isFinite(v) ? v : null;
        const raw = (typeof v === 'object' && v !== null) ? (v.value ?? '') : String(v);
        if (raw == null || raw === '') return null;
        const cleaned = String(raw).replace(/[,\s₹]/g, '').replace(/%/g, '').replace(/[^\d.-]/g, '');
        if (!cleaned || cleaned === '-') return null;
        const n = Number(cleaned);
        return Number.isFinite(n) ? n : null;
    };

    // Heuristic: treat a TTM value as missing if
    // 1) displayYear contains 'TTM'
    // 2) its numeric parsed value is 0 OR value is null/empty
    // 3) and ANY prior non‑TTM year for that ratio had a non‑zero numeric value.
    // This avoids plotting misleading zero drops for provisional TTM estimates.
    const isTTMMissing = (valueObj, allValueObjs) => {
        if (!valueObj) return false;
        const { displayYear, value } = valueObj;
        if (!displayYear || !String(displayYear).includes('TTM')) return false;
        const numeric = parseNum(value);
        const priorNonZero = allValueObjs
            .filter(v => v !== valueObj && v.displayYear && !String(v.displayYear).includes('TTM'))
            .some(v => parseNum(v.value) !== 0);
        const emptyLike = (value == null || value === '' || numeric === 0);
        return priorNonZero && emptyLike;
    };

    // Derive global dynamic years from all three statements
    const globalYearInfo = useMemo(() => {
        const p = extractYearSet(financials?.profitAndLoss);
        const b = extractYearSet(financials?.balanceSheet);
        const c = extractYearSet(financials?.cashFlow);
        const union = new Set([...p, ...b, ...c]);
        return buildDisplayHeadersFromSet(union, 11); // keep last 11 numeric + TTM
    }, [financials]);

    const displayCashflowRatios = useMemo(
        () => overlayStatementDerivedRatioValues(cashflowRatios, {
            data: financials,
            preferredBasis: basis,
            allRatios: [...profitlossRatios, ...balancesheetRatios, ...cashflowRatios],
        }),
        [cashflowRatios, financials, basis, profitlossRatios, balancesheetRatios],
    );
    const combinedRatios = useMemo(
        () => [...profitlossRatios, ...balancesheetRatios, ...displayCashflowRatios],
        [profitlossRatios, balancesheetRatios, displayCashflowRatios],
    );

    const cashFlowRatiosPanelResult = useMemo(() => {
        if (!financials) return null;
        return evaluateCashFlowRatiosPanel({
            sector,
            data: financials,
            allRatios: combinedRatios,
            preferredBasis: basis,
        });
    }, [financials, sector, basis, combinedRatios]);

    const profitLossRatiosPanelResult = useMemo(() => {
        if (!profitlossRatios.length) return null;
        return evaluateProfitLossRatiosPanel({
            sector,
            ratios: profitlossRatios,
            balanceSheetRatios: balancesheetRatios,
        });
    }, [sector, profitlossRatios, balancesheetRatios]);

    const balanceSheetRatiosPanelResult = useMemo(() => {
        if (!balancesheetRatios.length) return null;
        return evaluateBalanceSheetRatiosPanel({
            sector,
            ratios: balancesheetRatios,
        });
    }, [sector, balancesheetRatios]);

    const renderProfitandLossRatios = () => {
        if (!profitlossRatios.length || !profitlossRatios[0]?.values) return null;
        const panel = profitLossRatiosPanelResult;
        const profile = panel?.profile;
        const sourceRows = panel?.displayRatios || injectRoicWaccSpreadRow(profitlossRatios);
        const formatted = sourceRows.map(r => ({ ...r, values: formatRatioYears(r.values) }));
        const years = formatted[0].values.map(v => v.displayYear);
        const firstCol = 280, trendCol = 96, perYear = 64;
        const tableMinWidth = `${firstCol + trendCol + years.length * perYear}px`;

        return (
            <div className="ratios-section">
                <h3>Profit &amp; Loss Ratios</h3>
                {profile ? (
                    <>
                        <p className="fin-ratio-bench-caption">
                            Sector: {profile.sector.label} · Initial heuristic
                        </p>
                        <p className="fin-ratio-bench-methodology">
                            {panel.methodologyNote || profile.methodologyNote}
                        </p>
                    </>
                ) : null}
                <div className="table-wrapper">
                    <div className="table-scroll">
                        <table style={{ '--year-count': years.length, minWidth: tableMinWidth }}>
                            <thead>
                                <tr>
                                    <th>Ratio Name</th>
                                    <th>Trend</th>
                                    {years.map(y => <th key={y}>{y}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {formatted.map(ratio => {
                                    const series = ratio.values.map(v => isTTMMissing(v, ratio.values) ? null : parseNum(v?.value));
                                    const mapping = getProfitLossRatiosPanelMapping(ratio.name);
                                    const row = panel?.byRatioName?.[ratio.name];
                                    const kind = row?.kind || mapping.kind;
                                    const evaluation = row?.evaluation || null;
                                    const status = evaluation?.status || null;
                                    const showBadge = kind === 'scored';
                                    const rowClass = showBadge && status ? `fin-ratio-row fin-ratio-row--${status}` : 'fin-ratio-row';
                                    const yearPoints = showBadge
                                        ? evaluatePerYearProfitLossStatus({
                                            metricId: mapping.metricId || row?.metricId,
                                            sector,
                                            values: ratio.values.map(v => (isTTMMissing(v, ratio.values) ? null : parseFiniteOrNull(v?.value))),
                                            years: ratio.values.map(v => String(v.year)),
                                        })
                                        : [];
                                    const statusByYear = new Map(yearPoints.map((point) => [point.year, point.status]));
                                    const infoTitle = [
                                        ...(evaluation?.warnings || []),
                                        evaluation?.ruleText || 'View AI-powered definition',
                                    ].filter(Boolean).join(' — ');
                                    return (
                                        <tr key={ratio.name} className={rowClass}>
                                            <td>
                                                <div className="ratio-name-cell">
                                                    <div className="fin-ratio-name-meta">
                                                        <span>{formatRatioNameWithUnit(ratio)}</span>
                                                        {showBadge && status ? <CashFlowStatusBadge status={status} /> : null}
                                                    </div>
                                                    <div className="ratio-actions" style={{ display: 'flex', gap: '6px' }}>
                                                        <FontAwesomeIcon icon="circle-info" title={infoTitle} style={{ cursor: 'pointer' }} onClick={() => { setMetricInfoKey(ratio.name); setShowMetricInfo(true); }} />
                                                        <FontAwesomeIcon icon="chart-line" title="Open chart" style={{ cursor: 'pointer' }} onClick={() => { openChart(ratio); }} />
                                                        <FontAwesomeIcon icon="arrow-trend-up" title="Trend" style={{ cursor: 'pointer' }} onClick={() => { openChart(ratio); }} />
                                                    </div>
                                                </div>
                                            </td>
                                            <td><Sparkline values={series} /></td>
                                            {ratio.values.map(v => {
                                                const { displayYear, value, year } = v;
                                                const showDash = isTTMMissing(v, ratio.values);
                                                const cell = benchmarkYearCell(status, statusByYear.get(String(year)));
                                                return (
                                                    <td
                                                        key={displayYear}
                                                        className={cell.className}
                                                        title={cell.title}
                                                    >
                                                        {showDash ? '-' : (value ?? '-')}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    const renderBalanceSheetRatios = () => {
        if (!balancesheetRatios.length || !balancesheetRatios[0]?.values) return null;
        const panel = balanceSheetRatiosPanelResult;
        const profile = panel?.profile;
        const sourceRows = panel?.displayRatios || dedupeRatiosByName(balancesheetRatios);
        const formatted = sourceRows.map(r => ({ ...r, values: formatRatioYears(r.values) }));
        if (!formatted.length || !formatted[0]?.values) return null;
        const years = formatted[0].values.map(v => v.displayYear);
        const firstCol = 280, trendCol = 96, perYear = 64;
        const tableMinWidth = `${firstCol + trendCol + years.length * perYear}px`;

        return (
            <div className="ratios-section">
                <h3>Balance Sheet Ratios</h3>
                {profile ? (
                    <>
                        <p className="fin-ratio-bench-caption">
                            Sector: {profile.sector.label} · Initial heuristic
                        </p>
                        <p className="fin-ratio-bench-methodology">
                            {panel.methodologyNote || profile.methodologyNote}
                        </p>
                    </>
                ) : null}
                <div className="table-wrapper">
                    <div className="table-scroll">
                        <table style={{ '--year-count': years.length, minWidth: tableMinWidth }}>
                            <thead>
                                <tr>
                                    <th>Ratio Name</th>
                                    <th>Trend</th>
                                    {years.map(y => <th key={y}>{y}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {formatted.map(ratio => {
                                    const series = ratio.values.map(v => isTTMMissing(v, ratio.values) ? null : parseNum(v?.value));
                                    const mapping = getBalanceSheetRatiosPanelMapping(ratio.name);
                                    const row = panel?.byRatioName?.[ratio.name];
                                    const kind = row?.kind || mapping.kind;
                                    const evaluation = row?.evaluation || null;
                                    const status = evaluation?.status || null;
                                    const showBadge = kind === 'scored';
                                    const rowClass = showBadge && status ? `fin-ratio-row fin-ratio-row--${status}` : 'fin-ratio-row';
                                    const yearPoints = showBadge
                                        ? evaluatePerYearBalanceSheetStatus({
                                            metricId: mapping.metricId || row?.metricId,
                                            sector,
                                            values: ratio.values.map(v => (isTTMMissing(v, ratio.values) ? null : parseFiniteOrNull(v?.value))),
                                            years: ratio.values.map(v => String(v.year)),
                                        })
                                        : [];
                                    const statusByYear = new Map(yearPoints.map((point) => [point.year, point.status]));
                                    const infoTitle = [
                                        ...(evaluation?.warnings || []),
                                        evaluation?.ruleText || 'View AI-powered definition',
                                    ].filter(Boolean).join(' — ');
                                    return (
                                        <tr key={ratio.name} className={rowClass}>
                                            <td>
                                                <div className="ratio-name-cell">
                                                    <div className="fin-ratio-name-meta">
                                                        <span>{formatRatioNameWithUnit(ratio)}</span>
                                                        {showBadge && status ? <CashFlowStatusBadge status={status} /> : null}
                                                    </div>
                                                    <div className="ratio-actions" style={{ display: 'flex', gap: '6px' }}>
                                                        <FontAwesomeIcon icon="circle-info" title={infoTitle} style={{ cursor: 'pointer' }} onClick={() => { setMetricInfoKey(ratio.name); setShowMetricInfo(true); }} />
                                                        <FontAwesomeIcon icon="chart-line" title="Open chart" style={{ cursor: 'pointer' }} onClick={() => { openChart(ratio); }} />
                                                        <FontAwesomeIcon icon="arrow-trend-up" title="Trend" style={{ cursor: 'pointer' }} onClick={() => { openChart(ratio); }} />
                                                    </div>
                                                </div>
                                            </td>
                                            <td><Sparkline values={series} /></td>
                                            {ratio.values.map(v => {
                                                const { displayYear, value, year } = v;
                                                const showDash = isTTMMissing(v, ratio.values);
                                                const cell = benchmarkYearCell(status, statusByYear.get(String(year)));
                                                return (
                                                    <td
                                                        key={displayYear}
                                                        className={cell.className}
                                                        title={cell.title}
                                                    >
                                                        {showDash ? '-' : (value ?? '-')}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    const renderCashflowRatios = () => {
        if (!displayCashflowRatios.length || !displayCashflowRatios[0]?.values) return null;

        const formattedSets = displayCashflowRatios.map(r => ({
            ...r,
            values: formatRatioYears(r.values, 11)
        }));
        const displayYears = formattedSets[0].values.map(v => v.displayYear);

        const firstCol = 280, trendCol = 96, perYear = 64;
        const tableMinWidth = `${firstCol + trendCol + displayYears.length * perYear}px`;
        const panel = cashFlowRatiosPanelResult;
        const profile = panel?.profile;

        return (
            <div className="ratios-section">
                <h3>Cash Flow Ratios</h3>
                {profile ? (
                    <>
                        <p className="fin-ratio-bench-caption">
                            Sector: {profile.sector.label} · Initial heuristic
                        </p>
                        <p className="fin-ratio-bench-methodology">
                            {panel.methodologyNote || profile.methodologyNote}
                        </p>
                    </>
                ) : null}
                <div className="table-wrapper">
                    <div className="table-scroll">
                        <table style={{ '--year-count': displayYears.length, minWidth: tableMinWidth }}>
                            <thead>
                                <tr>
                                    <th>Ratio Name</th>
                                    <th>Trend</th>
                                    {displayYears.map(y => <th key={y}>{y}</th>)}
                                </tr>
                            </thead>
                            <tbody>
                                {formattedSets.map(ratio => {
                                    const mapping = getCashFlowRatiosPanelMapping(ratio.name);
                                    const row = panel?.byRatioName?.[ratio.name];
                                    const kind = row?.kind || mapping.kind;
                                    const evaluation = row?.evaluation || null;
                                    const status = evaluation?.status || null;
                                    const showBadge = kind === 'scored' || kind === 'selfSufficiency';
                                    const rowClass = showBadge && status ? `fin-ratio-row fin-ratio-row--${status}` : 'fin-ratio-row';
                                    const showDistress = kind === 'notScored' && hasDistressFlagRule(ratio.name);
                                    const yearStatuses = row?.yearStatuses || evaluation?.yearStatuses || [];
                                    const statusByYear = new Map(yearStatuses.map((point) => [String(point.year), point.status]));
                                    const distressFlags = showDistress
                                        ? evaluateDistressFlags({
                                            rowKey: ratio.name,
                                            values: ratio.values.map(v => (isTTMMissing(v, ratio.values) ? null : parseFiniteOrNull(v?.value))),
                                            years: ratio.values.map(v => String(v.year)),
                                        })
                                        : [];
                                    const distressByYear = new Map(distressFlags.map((flag) => [flag.year, flag]));
                                    const infoTitle = showDistress
                                        ? DISTRESS_FLAG_CAPTION
                                        : (evaluation?.ruleText || 'View AI-powered definition');
                                    const series = kind === 'selfSufficiency'
                                        ? ratio.values.map((v) => {
                                            if (isTTMMissing(v, ratio.values)) return null;
                                            const yearStatus = statusByYear.get(String(v.year));
                                            if (yearStatus === 'selfFunded') return 1;
                                            if (yearStatus === 'externallyFunded') return 0;
                                            return null;
                                        })
                                        : ratio.values.map(v => isTTMMissing(v, ratio.values) ? null : parseNum(v?.value));
                                    return (
                                        <tr key={ratio.name} className={rowClass}>
                                            <td>
                                                <div className="ratio-name-cell">
                                                    <div className="fin-ratio-name-meta">
                                                        <span title={showDistress ? DISTRESS_FLAG_CAPTION : undefined}>{formatRatioNameWithUnit(ratio)}</span>
                                                        {showBadge && status ? <CashFlowStatusBadge status={status} /> : null}
                                                    </div>
                                                    <div className="ratio-actions" style={{ display: 'flex', gap: '6px' }}>
                                                        <FontAwesomeIcon icon="circle-info" title={infoTitle} style={{ cursor: 'pointer' }}
                                                            onClick={() => { setMetricInfoKey(ratio.name); setShowMetricInfo(true); }} />
                                                        <FontAwesomeIcon icon="chart-line" title="Open chart" style={{ cursor: 'pointer' }}
                                                            onClick={() => { openChart(ratio); }} />
                                                        <FontAwesomeIcon icon="arrow-trend-up" title="Trend" style={{ cursor: 'pointer' }}
                                                            onClick={() => { openChart(ratio); }} />
                                                    </div>
                                                </div>
                                            </td>
                                            <td><Sparkline values={series} /></td>
                                            {ratio.values.map(v => {
                                                const { displayYear, value, year } = v;
                                                const showDash = isTTMMissing(v, ratio.values);
                                                const yearStatus = statusByYear.get(String(year));
                                                const flag = distressByYear.get(String(year));
                                                let cellClass;
                                                let cellTitle;
                                                let displayValue;
                                                if (showDash) {
                                                    displayValue = '-';
                                                } else if (kind === 'selfSufficiency') {
                                                    displayValue = yearStatus === 'selfFunded'
                                                        ? 'Yes'
                                                        : yearStatus === 'externallyFunded'
                                                            ? 'No'
                                                            : '-';
                                                    if (yearStatus === 'externallyFunded') {
                                                        cellClass = 'fin-year-cell fin-year-cell--watch';
                                                        cellTitle = evaluation?.ruleText;
                                                    }
                                                } else if (ABS_DISPLAY_CASHFLOW_ROWS.has(ratio.name)) {
                                                    const n = parseFiniteOrNull(value);
                                                    displayValue = n == null ? (value ?? '-') : Math.abs(n).toFixed(2);
                                                } else {
                                                    displayValue = value ?? '-';
                                                }
                                                if (kind === 'scored' && yearStatus === 'weak') {
                                                    const outlier = Boolean(status) && yearStatus !== status;
                                                    cellClass = `fin-year-cell fin-year-cell--weak${outlier ? ' fin-year-cell--outlier' : ''}`;
                                                    cellTitle = [evaluation?.ruleText, `This year: ${value}`].filter(Boolean).join(' — ');
                                                } else if (flag?.isDistress) {
                                                    cellClass = 'fin-year-cell fin-year-cell--weak';
                                                    cellTitle = flag.reason;
                                                }
                                                return (
                                                    <td
                                                        key={displayYear}
                                                        className={cellClass}
                                                        title={cellTitle}
                                                    >
                                                        {displayValue}
                                                    </td>
                                                );
                                            })}
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            </div>
        );
    };

    // Peer compare logic
    const allRatios = [...profitlossRatios, ...balancesheetRatios, ...displayCashflowRatios];
    const ratioOptions = allRatios.map(r => ({ value: r.name, label: r.displayName }));
    const peerOptions = peersList.map(p => ({ value: p.companyId, label: p.companyName }));
    const handlePeerSelect = (opts) => {
        setSelectedPeers(peersList.filter(p => opts.some(o => o.value === p.companyId)));
    };

    return (
        <div>
            {/* Unified Header for Company Detail */}
            <header className="unified-header company-header">
                <div className="header-brand" onClick={() => navigate('/home')}>
                    <img src={headerLogo} alt="Finlytics Forge" className="header-logo-svg" />
                    <span className="brand__text">
                        <span className="brand__title">Finlytics</span>
                        <span className="brand__subtitle">Forge</span>
                    </span>
                </div>
                <HeaderBackButton to="/dashboard" label="Stocks" />

                <div className="header-company-info">
                    <div className="company-name-row">
                        <h1 className="company-title">{companyName || 'Loading...'}</h1>
                        <span className="company-id">({id})</span>
                    </div>
                    <div className="company-metrics">
                        {currentPrice != null && (
                            <span className="metric-badge price" title="Current market price">
                                CMP ₹{Number(currentPrice).toLocaleString('en-IN')}
                            </span>
                        )}
                        {marketCap != null && (
                            <span className="metric-badge mktcap" title="Market capitalization">
                                MCap: {formatMarketCapFromRupees(marketCap)}
                            </span>
                        )}
                        {Number.isFinite(Number(sharesOutstanding)) && Number(sharesOutstanding) > 0 && (
                            <span className="metric-badge shares" title="Shares outstanding">
                                Shares: {formatSharesOutstanding(sharesOutstanding)}
                            </span>
                        )}
                    </div>
                </div>

                <div className="header-actions">
                    {/* Basis Toggle */}
                    <div className="basis-toggle-compact">
                        <button
                            className={`basis-btn ${basis === 'consolidated' ? 'active' : ''}`}
                            onClick={() => { setBasis('consolidated'); localStorage.setItem('basis', 'consolidated'); }}
                        >
                            Consolidated
                        </button>
                        <button
                            className={`basis-btn ${basis === 'standalone' ? 'active' : ''}`}
                            onClick={() => { setBasis('standalone'); localStorage.setItem('basis', 'standalone'); }}
                        >
                            Standalone
                        </button>
                    </div>

                    <button
                        className="action-btn theme-btn"
                        onClick={toggleTheme}
                        title={isDarkMode ? 'Light Mode' : 'Dark Mode'}
                    >
                        {isDarkMode ? '☀️' : '🌙'}
                    </button>

                    <div className="user-profile" onClick={handleMenuOpen}>
                        <span className="user-name">{getDisplayName()}</span>
                        <div className="user-avatar">
                            {profilePhotoLoading ? (
                                <div className="user-avatar-shimmer" />
                            ) : profilePhotoUrl ? (
                                <img src={profilePhotoUrl} alt={getDisplayName()} className="user-avatar-img" />
                            ) : (
                                getUserInitials()
                            )}
                        </div>
                    </div>
                </div>
            </header>

            <div className="company-detail-container">
                <div className="tab-bar-sticky-wrapper">
                    <div className="tab-bar">
                        {TABS.map((tab, index) => (
                            <button key={tab} ref={tabRefs.current[index]} className={activeTab === tab ? 'active' : ''} onClick={() => handleTabChange(tab)}>
                                {tab}
                            </button>
                        ))}
                        <div className="tab-indicator" style={indicatorStyle}></div>
                    </div>
                </div>

                <div key={activeTab} className={`tab-content${activeTab === 'Price' || activeTab === 'Technical' ? ' tab-content--price' : ''}`}>
                    {activeTab === 'Price' && (
                        <PriceChartTab
                            companyId={id}
                            currentPrice={currentPrice}
                            sharesOutstanding={sharesOutstanding}
                        />
                    )}
                    {activeTab === 'Technical' && (
                        <TechnicalTab companyId={id} />
                    )}
                    {activeTab === 'Profit and Loss' && (
                        <>
                            <FinancialComparisonsBlock
                                intro="Open growth, margin, and capital-efficiency charts from statement data."
                                placeholder="Choose a profit & loss comparison"
                                options={PROFIT_LOSS_COMPARISON_OPTIONS}
                                onSelect={openFinancialComparison}
                            />
                            <ProfitLossStatementTable
                                data={financials?.profitAndLoss}
                                growthAndReturn={financials?.growthAndReturn}
                                orderedKeys={globalYearInfo.orderedKeys}
                                headers={globalYearInfo.headers}
                                onMetricInfo={(label) => { setMetricInfoKey(label); setShowMetricInfo(true); }}
                                onOpenChart={openChart}
                            />
                            {renderProfitandLossRatios()}
                        </>
                    )}
                    {activeTab === 'Balance Sheet' && (
                        <>
                            <FinancialComparisonsBlock
                                intro="Open capital-structure, working-capital, and efficiency charts from statement data."
                                placeholder="Choose a balance-sheet comparison"
                                options={BALANCE_SHEET_COMPARISON_OPTIONS}
                                onSelect={openFinancialComparison}
                            />
                            <BalanceSheetStatementTable
                                data={financials?.balanceSheet}
                                orderedKeys={globalYearInfo.orderedKeys}
                                headers={globalYearInfo.headers}
                                onMetricInfo={(label) => { setMetricInfoKey(label); setShowMetricInfo(true); }}
                                onOpenChart={openChart}
                            />
                            {renderBalanceSheetRatios()}
                        </>
                    )}
                    {activeTab === 'Cash Flow' && (
                        <>
                            <FinancialComparisonsBlock
                                intro="Open owner-earnings, reinvestment, and capital-allocation charts from statement data."
                                placeholder="Choose a cash-flow comparison"
                                options={CASH_FLOW_COMPARISON_OPTIONS}
                                onSelect={openFinancialComparison}
                            />
                            <CashFlowStatementTable
                                data={financials?.cashFlow}
                                orderedKeys={globalYearInfo.orderedKeys}
                                headers={globalYearInfo.headers}
                                onMetricInfo={(label) => { setMetricInfoKey(label); setShowMetricInfo(true); }}
                                onOpenChart={openChart}
                            />
                            {renderCashflowRatios()}
                        </>
                    )}
                    {activeTab === 'Forensic' && (
                        <ForensicTab
                            report={forensicReport}
                            ratios={forensicRatios}
                            financials={financials}
                            companyName={companyName}
                            companyId={id}
                            basis={basis}
                            apiUrl={API_URL}
                        />
                    )}

                    {activeTab === 'DCF' && (
                        <div className="dcf-flex-container">
                            <div className="dcf-inputs-card">
                                <div className="dcf-header-row">
                                    <h2>DCF Calculator</h2>
                                    <div className="mode-switch">
                                        <button type="button" className={dcfMode === 'standard' ? 'seg active' : 'seg'} onClick={() => setDcfMode('standard')}>Standard</button>
                                        <button type="button" className={dcfMode === 'buffett' ? 'seg active' : 'seg'} onClick={() => setDcfMode('buffett')}>Buffett</button>
                                        <button type="button" className={dcfMode === 'pro' ? 'seg active' : 'seg'} onClick={() => { setDcfMode('pro'); setDcfResult(null); setAdvHistory([]); setAdvSensitivity([]); }}>Pro Auto</button>
                                        <button type="button" className={dcfMode === 'advPro' ? 'seg active' : 'seg'} onClick={() => { setDcfMode('advPro'); setDcfResult(null); setAdvHistory([]); setAdvSensitivity([]); }}>Adv Pro DCF</button>
                                    </div>
                                </div>
                                <p className="helper-text">Leverages historical series to auto-build FCFF and derive intrinsic valuation.</p>
                                {/* CONDITIONAL INPUTS */}
                                {dcfMode === 'standard' ? (
                                    <DCFInputs
                                        fcfHistory={fcfHistory}
                                        histMultiple={histTermMultiple}
                                        onChange={setStandardDcfInputs}
                                    />
                                ) : dcfMode === 'buffett' ? (
                                    <BuffettDCFInputs
                                        financials={financials}
                                        fcfHistory={fcfHistory}
                                        onChange={setBuffettDcfInputs}
                                        sharesOutstanding={sharesOutstanding}
                                    />
                                ) : dcfMode === 'advPro' ? (
                                    <div className="pro-form">
                                        <div className="form-grid">
                                            <div className="form-group">
                                                <label>Forecast Years</label>
                                                <input type="number" min="1" max="30" value={advDcfInputs.period} onChange={e => setAdvDcfInputs(d => ({ ...d, period: Number(e.target.value || 1) }))} />
                                            </div>
                                            <div className="form-group">
                                                <label>Historical Windows</label>
                                                <input type="number" min="1" max="25" value={advDcfInputs.years} onChange={e => setAdvDcfInputs(d => ({ ...d, years: Number(e.target.value || 1) }))} />
                                            </div>
                                            <div className="form-group">
                                                <label>Interval</label>
                                                <select value={advDcfInputs.interval} onChange={e => setAdvDcfInputs(d => ({ ...d, interval: e.target.value }))}>
                                                    <option value="annual">Annual</option>
                                                    <option value="quarter">Quarter</option>
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label>Discount Rate (%)</label>
                                                <input type="number" step="0.1" value={advDcfInputs.discount_rate * 100} onChange={e => setAdvDcfInputs(d => ({ ...d, discount_rate: Number(e.target.value || 0) / 100 }))} />
                                            </div>
                                            <div className="form-group">
                                                <label>Earnings Growth (%)</label>
                                                <input type="number" step="0.1" value={advDcfInputs.earnings_growth_rate * 100} onChange={e => setAdvDcfInputs(d => ({ ...d, earnings_growth_rate: Number(e.target.value || 0) / 100 }))} />
                                            </div>
                                            <div className="form-group">
                                                <label>CapEx Growth (%)</label>
                                                <input type="number" step="0.1" value={advDcfInputs.cap_ex_growth_rate * 100} onChange={e => setAdvDcfInputs(d => ({ ...d, cap_ex_growth_rate: Number(e.target.value || 0) / 100 }))} />
                                            </div>
                                            <div className="form-group">
                                                <label>Perpetual Growth (%)</label>
                                                <input type="number" step="0.1" value={advDcfInputs.perpetual_growth_rate * 100} onChange={e => setAdvDcfInputs(d => ({ ...d, perpetual_growth_rate: Number(e.target.value || 0) / 100 }))} />
                                            </div>
                                            <div className="form-group">
                                                <label>Sensitivity Step (%)</label>
                                                <input type="number" step="0.1" value={advDcfInputs.step_increase * 100} onChange={e => setAdvDcfInputs(d => ({ ...d, step_increase: Number(e.target.value || 0) / 100 }))} />
                                            </div>
                                            <div className="form-group">
                                                <label>Steps</label>
                                                <input type="number" min="0" max="20" value={advDcfInputs.steps} onChange={e => setAdvDcfInputs(d => ({ ...d, steps: Number(e.target.value || 0) }))} />
                                            </div>
                                            <div className="form-group">
                                                <label>Variable</label>
                                                <select value={advDcfInputs.variable || ''} onChange={e => setAdvDcfInputs(d => ({ ...d, variable: e.target.value || null }))}>
                                                    <option value="">None</option>
                                                    <option value="discount_rate">Discount Rate</option>
                                                    <option value="earnings_growth_rate">Earnings Growth</option>
                                                    <option value="cap_ex_growth_rate">CapEx Growth</option>
                                                    <option value="perpetual_growth_rate">Perpetual Growth</option>
                                                </select>
                                            </div>
                                        </div>
                                        {advSensitivity.length > 0 && (
                                            <div className="sens-section" style={{ marginTop: '0.75rem' }}>
                                                <div className="section-title">Sensitivity Sweep</div>
                                                <div className="table-scroll">
                                                    <table>
                                                        <thead>
                                                            <tr>
                                                                <th>Step</th>
                                                                <th>Value</th>
                                                                <th>Per Share</th>
                                                                <th>Equity (Cr)</th>
                                                                <th>EV (Cr)</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {advSensitivity.map((row, idx) => (
                                                                <tr key={idx}>
                                                                    <td>{row.step}</td>
                                                                    <td>{row.value?.toFixed(4)}</td>
                                                                    <td>{row.intrinsicSharePrice != null ? row.intrinsicSharePrice.toFixed(2) : '-'}</td>
                                                                    <td>{row.equityValue != null ? row.equityValue.toFixed(2) : '-'}</td>
                                                                    <td>{row.enterpriseValue != null ? row.enterpriseValue.toFixed(2) : '-'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}
                                        {advHistory.length > 0 && (
                                            <div className="sens-section" style={{ marginTop: '0.75rem' }}>
                                                <div className="section-title">Historical DCFs</div>
                                                <div className="table-scroll">
                                                    <table>
                                                        <thead>
                                                            <tr>
                                                                <th>As Of</th>
                                                                <th>Per Share</th>
                                                                <th>Equity (Cr)</th>
                                                                <th>EV (Cr)</th>
                                                            </tr>
                                                        </thead>
                                                        <tbody>
                                                            {advHistory.map((row, idx) => (
                                                                <tr key={idx}>
                                                                    <td>{row.asOf}</td>
                                                                    <td>{row.share_price != null ? row.share_price.toFixed(2) : '-'}</td>
                                                                    <td>{row.equity_value != null ? row.equity_value.toFixed(2) : '-'}</td>
                                                                    <td>{row.enterprise_value != null ? row.enterprise_value.toFixed(2) : '-'}</td>
                                                                </tr>
                                                            ))}
                                                        </tbody>
                                                    </table>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ) : (
                                    <div className="pro-form">
                                        <div className="form-grid">
                                            <div className="form-group">
                                                <label>WACC (%)</label>
                                                <input type="number" step="0.1" value={proDcfDefaults.wacc * 100} onChange={e => setProDcfDefaults(d => ({ ...d, wacc: Number(e.target.value || 0) / 100 }))} />
                                            </div>
                                            <div className="form-group">
                                                <label>Years</label>
                                                <input type="number" min="1" max="30" value={proDcfDefaults.projectionYears} onChange={e => setProDcfDefaults(d => ({ ...d, projectionYears: Number(e.target.value || 1) }))} />
                                            </div>
                                            <div className="form-group">
                                                <label>LTG (%)</label>
                                                <input type="number" step="0.1" value={proDcfDefaults.ltg * 100} onChange={e => setProDcfDefaults(d => ({ ...d, ltg: Number(e.target.value || 0) / 100 }))} />
                                            </div>
                                            <div className="form-group">
                                                <label>Multiple (x)</label>
                                                <input type="number" step="0.5" value={proDcfDefaults.exitMultiple} onChange={e => setProDcfDefaults(d => ({ ...d, exitMultiple: Number(e.target.value || 0) }))} />
                                            </div>
                                            <div className="form-group">
                                                <label>Exit Metric</label>
                                                <select value={proDcfDefaults.exitMetric} onChange={e => setProDcfDefaults(d => ({ ...d, exitMetric: e.target.value }))}>
                                                    <option value="FCF">FCF</option>
                                                    <option value="EBITDA">EBITDA</option>
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label>Base FCF</label>
                                                <select value={proDcfDefaults.baseFcfMethod} onChange={e => setProDcfDefaults(d => ({ ...d, baseFcfMethod: e.target.value }))}>
                                                    <option value="current">Current</option>
                                                    <option value="avg3">Avg 3</option>
                                                    <option value="avg5">Avg 5</option>
                                                    <option value="avg7">Avg 7</option>
                                                    <option value="avg10">Avg 10</option>
                                                </select>
                                            </div>
                                            <div className="form-group">
                                                <label>Growth</label>
                                                <select value={proDcfDefaults.growthChoice} onChange={e => setProDcfDefaults(d => ({ ...d, growthChoice: e.target.value }))}>
                                                    <option value="previousYear">YoY</option>
                                                    <option value="cagr3">CAGR 3</option>
                                                    <option value="cagr5">CAGR 5</option>
                                                    <option value="cagr7">CAGR 7</option>
                                                    <option value="cagr10">CAGR 10</option>
                                                </select>
                                            </div>
                                            <div className="form-group full-row mos-wrapper">
                                                <label>Margin of Safety <span className="mos-percent">{Math.round(proMoS * 100)}%</span></label>
                                                <input type="range" min={0} max={50} step={1} value={Math.round(proMoS * 100)} onChange={e => setProMoS(Number(e.target.value) / 100)} />
                                            </div>
                                        </div>

                                        <div className="advanced-toggle">
                                            <button type="button" onClick={() => setShowSensitivity(s => !s)} className="add-button secondary">{showSensitivity ? 'Hide Advanced' : 'Show Advanced'}</button>
                                        </div>
                                        {showSensitivity && (
                                            <div className="sens-section">
                                                <div className="section-title">Sensitivity</div>
                                                <div className="sens-grid">
                                                    <label>Mode
                                                        <select value={proSensMode} onChange={e => setProSensMode(e.target.value)}>
                                                            <option value="ltg">LTG Sweep</option>
                                                            <option value="exitMultiple">Exit Multiple Sweep</option>
                                                        </select>
                                                    </label>
                                                    <label>Output
                                                        <select value={proSensOutput} onChange={e => setProSensOutput(e.target.value)}>
                                                            <option value="equityValuePerShare">Per Share (?,1)</option>
                                                            <option value="equityValue">Equity (Cr)</option>
                                                            <option value="enterpriseValue">EV (Cr)</option>
                                                        </select>
                                                    </label>
                                                </div>
                                                <div className="sens-grid two-rows">
                                                    <label>WACC start
                                                        <input type="number" step="0.001" value={proSensWaccStart}
                                                            onChange={e => setProSensWaccStart(Number(e.target.value || 0))} />
                                                    </label>
                                                    <label>WACC stop
                                                        <input type="number" step="0.001" value={proSensWaccStop}
                                                            onChange={e => setProSensWaccStop(Number(e.target.value || 0))} />
                                                    </label>
                                                    <label>WACC step
                                                        <input type="number" step="0.001" value={proSensWaccStep}
                                                            onChange={e => setProSensWaccStep(Number(e.target.value || 0))} />
                                                    </label>
                                                    <label>{proSensMode === 'ltg' ? 'LTG' : 'Multiple'} start
                                                        <input type="number" step="0.001" value={proSensYStart}
                                                            onChange={e => setProSensYStart(Number(e.target.value || 0))} />
                                                    </label>
                                                    <label>{proSensMode === 'ltg' ? 'LTG' : 'Multiple'} stop
                                                        <input type="number" step="0.001" value={proSensYStop}
                                                            onChange={e => setProSensYStop(Number(e.target.value || 0))} />
                                                    </label>
                                                    <label>{proSensMode === 'ltg' ? 'LTG' : 'Multiple'} step
                                                        <input type="number" step="0.001" value={proSensYStep}
                                                            onChange={e => setProSensYStep(Number(e.target.value || 0))} />
                                                    </label>
                                                </div>
                                                <div className="action-row">
                                                    <button className="add-button" onClick={runProSensitivity}>Run</button>
                                                    <button className="add-button" onClick={() => setProSensResult(null)}>Clear</button>
                                                </div>

                                                {proSensResult && proSensResult.matrix && (
                                                    (() => {
                                                        const flat = proSensResult.matrix.flat().filter(v => v != null);
                                                        const minV = Math.min(...flat);
                                                        const maxV = Math.max(...flat);
                                                        const range = maxV - minV || 1;
                                                        const fmt = (v) => v == null ? '-' : v.toFixed(2);
                                                        const cellColor = (v) => {
                                                            if (v == null) return 'transparent';
                                                            const t = (v - minV) / range; // 0..1
                                                            let r, g, b;
                                                            if (t < 0.5) {
                                                                const k = t / 0.5;
                                                                r = 254;
                                                                g = Math.round(226 + (249 - 226) * k);
                                                                b = Math.round(226 + (195 - 226) * k);
                                                            } else {
                                                                const k = (t - 0.5) / 0.5;
                                                                r = Math.round(254 + (220 - 254) * k);
                                                                g = Math.round(249 + (252 - 249) * k);
                                                                b = Math.round(195 + (231 - 195) * k);
                                                            }
                                                            return `rgb(${r},${g},${b})`;
                                                        };
                                                        return (
                                                            <div className="sensitivity-wrapper" style={{ marginTop: '0.75rem' }}>
                                                                <div className="table-scroll sensitivity-grid">
                                                                    <table>
                                                                        <thead>
                                                                            <tr>
                                                                                <th>{proSensResult.yLabel} \ {proSensResult.xLabel}</th>
                                                                                {proSensResult.xValues.map((x, i) => (
                                                                                    <th key={i}>{x}</th>
                                                                                ))}
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody>
                                                                            {proSensResult.matrix.map((row, ri) => (
                                                                                <tr key={ri}>
                                                                                    <td>{proSensResult.yValues[ri]}</td>
                                                                                    {row.map((v, ci) => (
                                                                                        <td
                                                                                            key={ci}
                                                                                            style={{ backgroundColor: cellColor(v) }}
                                                                                            title={`${proSensResult.yLabel}: ${proSensResult.yValues[ri]}, ${proSensResult.xLabel}: ${proSensResult.xValues[ci]} => ${fmt(v)}`}
                                                                                        >
                                                                                            {fmt(v)}
                                                                                        </td>
                                                                                    ))}
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                                <div className="sensitivity-legend">
                                                                    <div className="legend-scale">
                                                                        <div className="legend-color" style={{ background: '#fee2e2' }}></div>
                                                                        <div className="legend-color" style={{ background: '#fef9c3' }}></div>
                                                                        <div className="legend-color" style={{ background: '#dcfce7' }}></div>
                                                                    </div>
                                                                    <div className="legend-labels">
                                                                        <span>Low</span><span>Mid</span><span>High</span>
                                                                    </div>
                                                                </div>
                                                            </div>
                                                        );
                                                    })()
                                                )}
                                            </div>
                                        )}
                                    </div>
                                )}
                                {/* Calculate Button */}
                                <button
                                    onClick={handleCalculate}
                                    disabled={
                                        (dcfMode === 'standard' && !standardDcfInputs) ||
                                        (dcfMode === 'buffett' && !buffettDcfInputs) ||
                                        (dcfMode === 'pro' && !financials) ||
                                        (dcfMode === 'advPro' && !advDcfInputs)
                                    }
                                    className="add-button primary full-width calc-btn"
                                >
                                    Calculate
                                </button>
                            </div>
                            <div className="dcf-results-card">
                                <h3 className="results-title">Results</h3>
                                {dcfResult ? (
                                    <div>
                                        <div className="intrinsic-block">
                                            <div className="intrinsic-label">Intrinsic Share Price</div>
                                            <div className="intrinsic-value">
                                                ₹ {(dcfResult.intrinsicSharePrice != null ? dcfResult.intrinsicSharePrice : dcfResult.equityValuePerShare) != null ? (dcfResult.intrinsicSharePrice != null ? dcfResult.intrinsicSharePrice : dcfResult.equityValuePerShare).toLocaleString() : '-'}
                                            </div>
                                        </div>
                                        <div className="dcf-result-row">
                                            <span>Buy Zone (₹)</span>
                                            <span className="dcf-result-value buy-zone">
                                                {dcfResult.buyZoneSharePrice?.low != null ? `Low: ₹${dcfResult.buyZoneSharePrice.low.toLocaleString()}` : '-'}
                                                {dcfResult.buyZoneSharePrice?.high != null && <span className="buy-high">High: ₹{dcfResult.buyZoneSharePrice.high.toLocaleString()}</span>}
                                            </span>
                                        </div>
                                    </div>
                                ) : (
                                    <div className="dcf-placeholder">Run a calculation to see results.</div>
                                )}
                            </div>
                        </div>
                    )}

                    {activeTab === 'Valuation' && (
                        <ValuationPanel
                            financials={financials}
                            currentPrice={currentPrice}
                            sharesOutstanding={sharesOutstanding}
                            sector={sector}
                        />
                    )}


                    {/* Peer Compare Tab */}
                    {activeTab === 'Peer Compare' && (
                        <div className="peer-compare-section">
                            <div className="peer-selectors" style={{ display: 'flex', gap: '1rem', alignItems: 'center', margin: '1rem 0' }}>
                                <div>
                                    <label>Ratio:&nbsp;
                                        <select value={selectedPeerRatio} onChange={e => setSelectedPeerRatio(e.target.value)}>
                                            <option value="">Select ratio</option>
                                            {ratioOptions.map(opt => <option key={opt.value} value={opt.value}>{opt.label}</option>)}
                                        </select>
                                    </label>
                                </div>
                                <div style={{ flex: 1 }}>
                                    <label>Select Peers:</label>
                                    <Select
                                        isMulti
                                        options={peerOptions}
                                        value={peerOptions.filter(o => selectedPeers.some(p => p.companyId === o.value))}
                                        onChange={handlePeerSelect}
                                        placeholder="Select peer companies..."
                                    />
                                </div>
                            </div>
                            {selectedPeerRatio && (
                                <div className="chart-container" style={{ height: '400px' }}>
                                    <PeerChartModal
                                        isOpen={true}
                                        asInline={true} // <-- Add this prop for inline rendering
                                        onClose={() => { }}
                                        selectedPeerRatio={selectedPeerRatio}
                                        allRatios={allRatios}
                                        selectedPeers={selectedPeers}
                                        companyName={companyName}
                                        baseCompanyRatios={{ [selectedPeerRatio]: allRatios.find(r => r.name === selectedPeerRatio)?.values }}
                                    />
                                </div>
                            )}
                        </div>
                    )}



                    {activeTab === 'Working Capital' && (
                        <div className="working-capital-section">
                            {!wcData ? (
                                <div className="loading-spinner">Loading Working Capital data...</div>
                            ) : wcData.error ? (
                                <div className="error-message">Error loading working capital dashboard.</div>
                            ) : (
                                <div className="p-6 space-y-6">
                                    {/* Timeframe Info Banner */}
                                    <div style={{
                                        padding: '12px 16px',
                                        backgroundColor: '#eff6ff',
                                        border: '1px solid #bfdbfe',
                                        borderRadius: '8px',
                                        fontSize: '13px',
                                        color: '#1e40af',
                                        marginBottom: '16px'
                                    }}>
                                        <strong>ℹ️ Understanding Timeframes:</strong> The attribution banner shows <strong>3-year trends</strong> to reveal longer-term patterns,
                                        while the Key Metrics cards show <strong>year-over-year changes</strong> for immediate performance assessment.
                                    </div>

                                    {/* Explain Section */}
                                    {wcExplain && !wcExplain.error && (
                                        <div className="wc-block" style={{ marginBottom: '24px' }}>
                                            <ReasonBanner verdict={wcExplain.verdict} statement={wcExplain.statement} />
                                            <AttributionBar
                                                daysEffect={wcExplain?.attribution?.days_effect}
                                                growthEffect={wcExplain?.attribution?.growth_effect}
                                                companyName={companyName}
                                                dsoDelta={wcExplain?.evidence?.dso_delta_days}
                                                dioDelta={wcExplain?.evidence?.dio_delta_days}
                                                dpoDelta={wcExplain?.evidence?.dpo_delta_days}
                                                cccDelta={wcExplain?.evidence?.ccc_delta_days}
                                            />
                                            <EvidenceChips
                                                dsoDelta={wcExplain?.evidence?.dso_delta_days}
                                                dioDelta={wcExplain?.evidence?.dio_delta_days}
                                                dpoDelta={wcExplain?.evidence?.dpo_delta_days}
                                                cccDelta={wcExplain?.evidence?.ccc_delta_days}
                                            />
                                        </div>
                                    )}

                                    {/* Cash Flow Movement Waterfall */}
                                    {wcData.cashFlowMovementByYear && selectedCashBridgeYear && (
                                        <div style={{ marginBottom: '24px' }}>
                                            <CashBridgeWaterfall
                                                cashBridge={wcData.cashFlowMovementByYear[selectedCashBridgeYear]}
                                                year={selectedCashBridgeYear}
                                                availableYears={wcData.years}
                                                onYearChange={(year) => setSelectedCashBridgeYear(year)}
                                            />
                                        </div>
                                    )}

                                    {/* KPIs Section */}
                                    {wcData.kpis && (
                                        <div style={{ marginBottom: '24px' }}>
                                            <h2 className="text-lg font-semibold mb-4">
                                                📊 Key Metrics
                                                <span style={{ fontSize: '14px', color: '#6b7280', fontWeight: 'normal', marginLeft: '8px' }}>
                                                    (Year-over-Year Changes)
                                                </span>
                                            </h2>
                                            <KPISection kpis={normalizeKPIs(wcData.kpis)} metricsByYear={wcData.metricsByYear} />
                                        </div>
                                    )}

                                    {/* CCC Waterfall - Visual breakdown of Cash Conversion Cycle */}
                                    {wcData.kpis && (
                                        <div style={{ marginBottom: '24px' }}>
                                            <h2 className="text-lg font-semibold mb-3">🔄 Cash Conversion Cycle Build-up</h2>
                                            <CCCWaterfallChart
                                                dso={wcData.kpis.dso}
                                                dio={wcData.kpis.dio}
                                                dpo={wcData.kpis.dpo}
                                                ccc={wcData.kpis.ccc}
                                            />
                                        </div>
                                    )}

                                    {/* Working Capital Efficiency Metrics */}
                                    {wcData.efficiencyMetrics && (
                                        <div style={{ marginBottom: '24px' }}>
                                            <h2 className="text-lg font-semibold mb-4">⚡ Working Capital Efficiency</h2>
                                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                                                <div style={{
                                                    padding: '20px',
                                                    backgroundColor: '#f0f9ff',
                                                    borderRadius: '12px',
                                                    border: '2px solid #0ea5e9'
                                                }}>
                                                    <div style={{ fontSize: '14px', color: '#0369a1', fontWeight: '600', marginBottom: '8px' }}>
                                                        Working Capital as % of Sales
                                                    </div>
                                                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#0c4a6e' }}>
                                                        {wcData.efficiencyMetrics.wcAsPctSales.toFixed(1)}%
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#0369a1', marginTop: '8px' }}>
                                                        Lower is better (less cash tied up)
                                                    </div>
                                                </div>
                                                <div style={{
                                                    padding: '20px',
                                                    backgroundColor: '#f0fdf4',
                                                    borderRadius: '12px',
                                                    border: '2px solid #10b981'
                                                }}>
                                                    <div style={{ fontSize: '14px', color: '#047857', fontWeight: '600', marginBottom: '8px' }}>
                                                        Working Capital Turnover
                                                    </div>
                                                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#065f46' }}>
                                                        {wcData.efficiencyMetrics.wcTurnover.toFixed(2)}x
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#047857', marginTop: '8px' }}>
                                                        Higher is better (efficient usage)
                                                    </div>
                                                </div>
                                                <div style={{
                                                    padding: '20px',
                                                    backgroundColor: '#fef3c7',
                                                    borderRadius: '12px',
                                                    border: '2px solid #f59e0b'
                                                }}>
                                                    <div style={{ fontSize: '14px', color: '#b45309', fontWeight: '600', marginBottom: '8px' }}>
                                                        Trade Working Capital
                                                    </div>
                                                    <div style={{ fontSize: '32px', fontWeight: 'bold', color: '#78350f' }}>
                                                        ₹{wcData.efficiencyMetrics.tradeWC.toLocaleString('en-IN')}
                                                    </div>
                                                    <div style={{ fontSize: '12px', color: '#b45309', marginTop: '8px' }}>
                                                        AR + Inventory - AP (in Crores)
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    )}

                                    {/* Working Capital Components */}
                                    {wcData.waterfall && Array.isArray(wcData.waterfall) && wcData.waterfall.length > 0 && (
                                        <div style={{ marginBottom: '24px' }}>
                                            <h2 className="text-lg font-semibold mb-3">💧 Working Capital Components</h2>
                                            <div className="space-y-2">
                                                {wcData.waterfall.map((item, idx) => (
                                                    <div key={idx} style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        padding: '12px 16px',
                                                        backgroundColor: '#f9fafb',
                                                        borderRadius: '8px',
                                                        border: '1px solid #e5e7eb'
                                                    }}>
                                                        <span style={{ fontWeight: '500' }}>{item.label}</span>
                                                        <span style={{
                                                            fontWeight: 'bold',
                                                            color: item.value >= 0 ? '#059669' : '#dc2626'
                                                        }}>
                                                            ₹{Math.abs(item.value).toLocaleString('en-IN')} Cr
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Historical Trends */}
                                    {wcData.trends && (
                                        <div style={{ marginBottom: '24px' }}>
                                            <h2 className="text-lg font-semibold mb-3">📈 Historical Trends</h2>
                                            <RatioTrends data={wcData.trends} />
                                        </div>
                                    )}

                                    {/* Working Capital Components - Detailed breakdown */}
                                    {wcData.waterfall && Array.isArray(wcData.waterfall) && wcData.waterfall.length > 0 && (
                                        <div style={{ marginBottom: '24px' }}>
                                            <h2 className="text-lg font-semibold mb-3">💼 Working Capital Components (Latest Year)</h2>
                                            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
                                                Breakdown of all current assets and liabilities comprising net working capital
                                            </p>
                                            <div className="space-y-2">
                                                {wcData.waterfall.map((item, idx) => (
                                                    <div key={idx} style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        padding: '12px 16px',
                                                        backgroundColor: item.label === 'Net Working Capital' ? '#eff6ff' : '#f9fafb',
                                                        borderRadius: '8px',
                                                        border: item.label === 'Net Working Capital' ? '2px solid #3b82f6' : '1px solid #e5e7eb'
                                                    }}>
                                                        <span style={{ fontWeight: item.label === 'Net Working Capital' ? 'bold' : '500' }}>
                                                            {item.label}
                                                        </span>
                                                        <span style={{
                                                            fontWeight: 'bold',
                                                            color: item.value >= 0 ? '#059669' : '#dc2626'
                                                        }}>
                                                            ₹{Math.abs(item.value).toLocaleString('en-IN')} Cr
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}

                                    {/* Change Drivers - Show as list */}
                                    {wcExplain?.changeWaterfall && Array.isArray(wcExplain.changeWaterfall) && wcExplain.changeWaterfall.length > 0 && (
                                        <div style={{ marginBottom: '24px' }}>
                                            <h2 className="text-lg font-semibold mb-3">📉 Working Capital Changes (Last {wcExplain?.lookback || 3} Years)</h2>
                                            <p style={{ fontSize: '14px', color: '#6b7280', marginBottom: '12px' }}>
                                                How changes in WC components impacted cash flow (negative = cash outflow)
                                            </p>
                                            <div className="space-y-2">
                                                {wcExplain.changeWaterfall.map((item, idx) => (
                                                    <div key={idx} style={{
                                                        display: 'flex',
                                                        justifyContent: 'space-between',
                                                        alignItems: 'center',
                                                        padding: '12px 16px',
                                                        backgroundColor: '#f9fafb',
                                                        borderRadius: '8px',
                                                        border: '1px solid #e5e7eb'
                                                    }}>
                                                        <span style={{ fontWeight: '500' }}>{item.label}</span>
                                                        <span style={{
                                                            fontWeight: 'bold',
                                                            color: item.value >= 0 ? '#059669' : '#dc2626'
                                                        }}>
                                                            {item.value >= 0 ? '+' : ''}₹{item.value.toLocaleString('en-IN')} Cr
                                                        </span>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    )}

                    {activeTab === 'Investment Intelligence' && (
                        <InvestmentIntelligenceTab companyId={id} basis={basis} />
                    )}

                    {activeTab === 'AI Research' && (
                        <AiResearchTab
                            companyId={id}
                            basis={basis}
                            apiUrl={API_URL}
                            currentTab={activeTab}
                        />
                    )}

                    {activeTab === 'Ask AI' && (
                        <ErrorBoundary onReset={() => console.log('[Ask AI] Error boundary reset')}>
                            <div style={{ height: 'calc(100vh - 160px)', minHeight: '600px' }}>
                                {id && companyName ? (
                                    <AskFinlyticsChat
                                        companyId={id}
                                        companyName={companyName}
                                        availableYears={globalYearInfo.headers?.map(y => parseInt(y.replace('FY', '20'))) || []}
                                    />
                                ) : (
                                    <div style={{
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        height: '100%',
                                        color: 'var(--text-2)'
                                    }}>
                                        Loading company data...
                                    </div>
                                )}
                            </div>
                        </ErrorBoundary>
                    )}

                    {activeTab === 'News' && (
                        <NewsTab companyId={id} apiUrl={API_URL} />
                    )}

                    {activeTab === 'About' && (
                        <AboutTab companyId={id} scope={basis} apiUrl={API_URL} />
                    )}

                    {activeTab === 'Admin Operations' && (
                        <AdminOperations companyId={id} companyName={companyName} />
                    )}

                </div>
            </div>

            <RatioDetailModal isOpen={showModal} ratio={selectedRatio} onClose={() => setShowModal(false)} />
            <ChartModal
                isOpen={showChartModal}
                ratio={chartRatio}
                comparisonId={chartComparisonId}
                allRatios={combinedRatios}
                financials={financials}
                companyName={companyName}
                preferredBasis={basis}
                sector={sector}
                onClose={closeChartModal}
            />
            <PeerChartModal
                isOpen={showPeerChartModal}
                onClose={() => setShowPeerChartModal(false)}
                selectedPeerRatio={selectedPeerRatio}
                allRatios={allRatios}
                selectedPeers={selectedPeers}
                companyName={companyName}
                baseCompanyRatios={{ [selectedPeerRatio]: allRatios.find(r => r.name === selectedPeerRatio)?.values }}
            />
            {showMetricInfo && (
                <MetricInfoModal
                    metricKey={metricInfoKey}
                    entityType="nonFinancial"
                    scope={basis}
                    apiUrl={API_URL}
                    onClose={() => { setShowMetricInfo(false); setMetricInfoKey(null); }}
                />
            )}
        </div>
    );
};

export default CompanyDetail;

